import React, { useEffect, useState, useContext } from 'react';
import '../../styles/ExpertAverageRating.css';
import { UrlContext } from "../../contexts/UrlContext";
import { AnimatePresence, motion } from "framer-motion";
import { FaTimes } from 'react-icons/fa';
import FiveStarRatingModal from '../FiveStarRatingModal';

export default function ExpertAverageRating({ expertId, callingFrom = null }) {
    const { API_URL } = useContext(UrlContext);
    const [averageRating, setAverageRating] = useState(null);
    const [ratingCount, setRatingCount] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [pastRatings, setPastRatings] = useState([]);
    const [loadingRatings, setLoadingRatings] = useState(false);
    const [errorRatings, setErrorRatings] = useState(null);
    const [showRatingModal, setShowRatingModal] = useState(false);
    const [showRatingOnly, setShowRatingOnly] = useState(false);
    const [expert, setExpert] = useState(null);

    useEffect(() => {
        if (!expertId) return;
        fetchExpertRatings();
    }, [expertId]);

    async function fetchExpertRatings() {
        fetch(`${API_URL}get-expert-average-rating?expertId=${expertId}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        })
            .then(res => res.json())
            .then(data => {
                if (data.status === 'success') {
                    setAverageRating(data.averageRating);
                    setRatingCount(data.ratingCount);
                }
            });
    }

    // Fetch past ratings when modal is opened
    useEffect(() => {
        if (showModal && expertId) {
            setLoadingRatings(true);
            setErrorRatings(null);
            fetch(`${API_URL}get-expert-past-ratings?expertId=${expertId}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            })
                .then(res => res.json())
                .then(data => {
                    console.log('out', data);
                    if (data.status === 'success') {
                        console.log(data);
                        setPastRatings(data.ratings || []);
                        if (data.expert) {
                            setExpert(data.expert);
                        }
                    } else {
                        setErrorRatings('Failed to load ratings.');
                    }
                    setLoadingRatings(false);
                })
                .catch(() => {
                    setErrorRatings('Failed to load ratings.');
                    setLoadingRatings(false);
                });
        }
    }, [showModal, expertId, API_URL]);

    useEffect(() => {
        if (showRatingOnly && expertId) {
            // setLoadingRatings(true);
            // setErrorRatings(null);
            fetch(`${API_URL}get-expert-name?expertId=${expertId}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            })
                .then(res => res.json())
                .then(data => {
                    if (data.status === 'success') {
                        console.log('expert', data.expert);
                        if (data.expert) {
                            setExpert(data.expert);
                        }
                    } else {
                        console.error('Failed to load expert name:', data.message);
                    }
                    setShowRatingModal(true);

                })
                .catch(() => {
                    console.error('Failed to load expert name');
                });
        }
    }, [showRatingOnly, expertId, API_URL]);

    // Calculate fill percentage for each star
    const getStarFill = (starIndex) => {
        if (averageRating === null) return 0;
        const full = starIndex + 1;
        if (averageRating >= full) return 100;
        if (averageRating > starIndex) return (averageRating - starIndex) * 100;
        return 0;
    };

    // Helper to get relative time string
    function getRelativeTime(dateString) {
        if (!dateString) return '';
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffSec = Math.floor(diffMs / 1000);
        const diffMin = Math.floor(diffSec / 60);
        const diffHour = Math.floor(diffMin / 60);
        const diffDay = Math.floor(diffHour / 24);
        const diffMonth = Math.floor(diffDay / 30);
        const diffYear = Math.floor(diffDay / 365);
        if (diffYear > 0) return diffYear === 1 ? '1 year ago' : `${diffYear} years ago`;
        if (diffMonth > 0) return diffMonth === 1 ? '1 month ago' : `${diffMonth} months ago`;
        if (diffDay > 0) return diffDay === 1 ? '1 day ago' : `${diffDay} days ago`;
        if (diffHour > 0) return diffHour === 1 ? '1 hour ago' : `${diffHour} hours ago`;
        if (diffMin > 0) return diffMin === 1 ? '1 minute ago' : `${diffMin} minutes ago`;
        return 'just now';
    }

    // Submit new rating and refresh list
    async function handleSubmitRating(ratings, comments) {
        // You may need to adjust the API endpoint and payload as per your backend
        try {
            const ephToken = localStorage.getItem("eph-token");
            const token = JSON.parse(ephToken).value;
            const response = await fetch(`${API_URL}submit-expert-ratings`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', "x-access-token": token },
                body: JSON.stringify({
                    "ratings": [{
                        expertId,
                        rating: ratings[expertId],
                        comment: comments[expertId],
                    }]
                }),
            });
            const data = await response.json();
            // Optionally show a success message here
        } catch (e) {
            // Optionally show an error message here
        }
        if (!showModal && showRatingModal && !showRatingOnly) {
            setShowModal(true);
            setShowRatingModal(false);

        } else if (showRatingOnly && showRatingModal) {
            setShowRatingModal(false);
            setShowRatingOnly(false);
        }
        await fetchExpertRatings();

    }

    const Star = ({ size = 20, fillPercent = 0, animate = true }) => {
        const containerStyle = { position: 'relative', display: 'inline-block', width: size, height: size };
        const overlayStyle = { position: 'absolute', left: 0, top: 0, width: '100%', height: '100%' };
        return (
            <span style={containerStyle}>
                <motion.div
                    style={{ position: 'absolute', left: 0, top: 0, height: size, overflow: 'hidden' }}
                    initial={{ width: 0 }}
                    animate={{ width: `${fillPercent}%` }}
                    transition={animate ? { duration: 0.5, ease: 'easeOut' } : { duration: 0 }}
                >
                    <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 .587l3.668 7.431 8.2 1.193-5.934 5.787 1.401 8.168L12 18.896l-7.335 3.87 1.401-8.168L.132 9.211l8.2-1.193z" fill="#FFD700" />
                    </svg>
                </motion.div>
                <div style={overlayStyle}>
                    <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 .587l3.668 7.431 8.2 1.193-5.934 5.787 1.401 8.168L12 18.896l-7.335 3.87 1.401-8.168L.132 9.211l8.2-1.193z" fill="transparent" stroke="#ccc" strokeWidth="1.2" />
                    </svg>
                </div>
            </span>
        );
    };

    return (
        <div
            className="expert-average-rating multi-star"
            style={{ display: 'inline-block', position: 'relative' }}
        >
            <span className="star-wrapper" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ color: '#888', fontSize: '14px' }}>
                    {averageRating !== null ? averageRating.toFixed(2) : '--'}
                </span>
                {[0, 1, 2, 3, 4].map((i) => (
                    <span key={i}>
                        <Star size={20} fillPercent={getStarFill(i)} />
                    </span>
                ))}
                <span
                    className={'eph-rating-count-link'}
                    style={{ color: '#365561', fontSize: '14px', cursor: 'pointer' }}
                    onClick={() => {
                        if (ratingCount > 0) {
                            setShowModal(true);
                            setShowRatingOnly(false);
                        } else {
                            setShowRatingOnly(true);
                        }
                    }}
                    title={ratingCount > 0 ? 'View past ratings & comments' : 'Write a review'}
                >
                    ({ratingCount !== null ? ratingCount : '--'})
                </span>
            </span>
            {/* Only show the past ratings/comments modal if not showing the rating modal */}
            {showModal && !showRatingModal && (
                <div className="eph-modal-overlay">
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.9, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="eph-modal-content"
                    >
                        <button
                            onClick={() => setShowModal(false)}
                            className="eph-modal-close-btn"
                            aria-label="Close"
                        >
                            <FaTimes />
                        </button>
                        <div className="eph-modal-header-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
                            <h2 className="eph-modal-title">Past Ratings & Comments</h2>
                            <button
                                className="btn base-btn"
                                style={{ marginBottom: 0, marginRight: 20 }}
                                onClick={() => {
                                    setShowModal(false);
                                    setShowRatingModal(true);
                                }}
                            >
                                Write a Review
                            </button>
                        </div>
                        {loadingRatings ? (
                            <div className="eph-modal-loading">Loading...</div>
                        ) : errorRatings ? (
                            <div className="eph-modal-error">{errorRatings}</div>
                        ) : pastRatings.length === 0 ? (
                            <div className="eph-modal-empty">No ratings or comments found.</div>
                        ) : (
                            <div className="eph-modal-pastratings-scroll">
                                {pastRatings
                                    .slice() // copy to avoid mutating state
                                    .sort((a, b) => new Date(b.date) - new Date(a.date))
                                    .map((r, idx) => (
                                        <div key={idx} className="eph-modal-rating-row">
                                            <div className="eph-modal-rating-header">
                                                {/* Stars for the rating */}
                                                <span style={{ display: 'flex', alignItems: 'center', gap: 2, marginRight: 8 }}>
                                                    {[0, 1, 2, 3, 4].map((i) => (
                                                        <span key={i}>
                                                            <Star
                                                                size={16}
                                                                fillPercent={r.rating >= i + 1 ? 100 : r.rating > i ? (r.rating - i) * 100 : 0}
                                                                animate={false}
                                                            />
                                                        </span>
                                                    ))}
                                                </span>
                                                {/* <span className="eph-modal-rating-star">★ {r.rating.toFixed(1)}</span> */}
                                                <span className="eph-modal-rating-date">{r.date ? getRelativeTime(r.date) : ''}</span>
                                            </div>
                                            <div className="eph-modal-rating-comment">{r.comment || <span style={{ color: '#aaa' }}>No comment</span>}</div>
                                            {r.reviewer && <div className="eph-modal-rating-reviewer">By: {r.reviewer}{r.source === 'client' && <span className="ml-2 text-xs bg-blue-100 text-blue-800 rounded px-1">Client</span>}</div>}
                                        </div>
                                    ))}
                            </div>
                        )}
                    </motion.div>
                </div>
            )}
            {/* Show the FiveStarRatingModal on top if needed */}
            {showRatingModal && (
                <FiveStarRatingModal
                    experts={expert ? [expert] : []}
                    onSubmit={handleSubmitRating}
                    onSkip={() => {
                        setShowRatingModal(false);
                        setShowRatingOnly(false);
                    }}
                />
            )}
        </div>
    );
} 